import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useMasters, nameOf } from "@/lib/masters";
import { PageHeader, Card, Input, Select, Field, Btn, Chip } from "@/components/kit";
import { exportXlsx, exportCsv } from "@/lib/export";
import { istDateString } from "@/lib/date";
import { useAuth } from "@/lib/auth";

function exportCsvOld(filename, rows, headers) {
  const csv = [headers.join(","), ...rows.map(r => headers.map(h => JSON.stringify(r[h] ?? "")).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function DailyReport() {
  const { user } = useAuth();
  const m = useMasters();
  const canManageAllWorkshops = ["super_admin", "admin", "manager"].includes(user?.role);
  const assignedWorkshop = (user?.assigned_workshop || "AMSKY").toUpperCase();
  const today = istDateString();
  const [date, setDate] = useState(today);
  const [filter, setFilter] = useState({ machine_id: "", plate_size_id: "", location_id: "" });
  const [rows, setRows] = useState([]);
  const latestRequest = useRef(0);
  const defaultsApplied = useRef(false);

  useEffect(() => {
    if (defaultsApplied.current) return;
    if (canManageAllWorkshops) {
      defaultsApplied.current = true;
      return;
    }
    if (!(m.machines || []).length || !(m.locations || []).length) return;
    const machine = m.machines.find(item => item.name?.toUpperCase() === assignedWorkshop || item.code?.toUpperCase() === assignedWorkshop);
    const location = m.locations.find(item => item.name?.toUpperCase() === assignedWorkshop);
    setFilter(current => ({ ...current, machine_id: machine?.id || "", location_id: location?.id || "" }));
    defaultsApplied.current = true;
  }, [assignedWorkshop, canManageAllWorkshops, m.locations, m.machines]);

  const load = useCallback(async () => {
    const requestId = ++latestRequest.current;
    const params = { date };
    Object.entries(filter).forEach(([k,v]) => { if (v) params[k] = v; });
    const { data } = await api.get("/inventory/daily_report", { params });
    if (requestId === latestRequest.current) setRows(data);
  }, [date, filter]);
  useEffect(() => { load(); }, [load]);

  const flat = rows.map(r => ({
    Workshop: nameOf(m.machines, r.machine_id),
    "Plate Size": m.plate_sizes.find(p=>p.id===r.plate_size_id)?.canonical || "-",
    Location: nameOf(m.locations, r.location_id),
    Opening: r.opening, Purchase: r.purchase, Consumption: r.consumption,
    "Cons.Rev": r.consumption_reversal, "Adj.In": r.adjustment_in, "Adj.Out": r.adjustment_out,
    "Trf.In": r.transfer_in, "Trf.Out": r.transfer_out, Damage: r.damage, Closing: r.closing,
  }));

  return (
    <div data-testid="daily-report-page">
      <PageHeader
        title="Daily Inventory Report"
        subtitle="Opening balance = sum of all movements before this date. Closing = opening + today's signed movements."
        actions={<div className="flex gap-2">
          <Btn variant="outline" onClick={()=>exportCsv(`daily_${date}.csv`, flat)} data-testid="daily-export-csv">Export CSV</Btn>
          <Btn onClick={()=>exportXlsx(`daily_${date}.xlsx`, flat, `Daily ${date}`)} data-testid="daily-export-xlsx">Export XLSX</Btn>
        </div>}
      />
      <div className="p-3 sm:p-6 space-y-4 min-w-0">
        <Card>
          <div className="p-3 flex flex-wrap gap-3 items-end">
            <Field label="Date"><Input type="date" value={date} onChange={e=>setDate(e.target.value)} data-testid="daily-date-input" /></Field>
            <Field label="Workshop">
              <Select value={filter.machine_id} onChange={e=>{
                const machine = m.machines.find(item => item.id === e.target.value);
                const location = machine ? m.locations.find(item => item.name?.toUpperCase() === machine.name?.toUpperCase()) : null;
                setFilter({...filter, machine_id:e.target.value, location_id:location?.id || ""});
              }}>
                <option value="">All</option>{m.machines.map(x=><option key={x.id} value={x.id}>{x.name}</option>)}
              </Select>
            </Field>
            {!canManageAllWorkshops && filter.machine_id && nameOf(m.machines, filter.machine_id).toUpperCase() !== assignedWorkshop && <Chip tone="warn">View only</Chip>}
            <Field label="Plate Size">
              <Select value={filter.plate_size_id} onChange={e=>setFilter({...filter,plate_size_id:e.target.value})}>
                <option value="">All</option>{m.plate_sizes.map(x=><option key={x.id} value={x.id}>{x.canonical}</option>)}
              </Select>
            </Field>
            <Field label="Location">
              <Select value={filter.location_id} onChange={e=>setFilter({...filter,location_id:e.target.value})}>
                <option value="">All</option>{m.locations.map(x=><option key={x.id} value={x.id}>{x.name}</option>)}
              </Select>
            </Field>
          </div>
        </Card>
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full data-table" data-testid="daily-report-table">
              <thead><tr>
                <th className="px-2 py-2 text-left">Workshop</th>
                <th className="px-2 py-2 text-left">Size</th>
                <th className="px-2 py-2 text-left">Location</th>
                <th className="px-2 py-2 text-right">Opening</th>
                <th className="px-2 py-2 text-right text-emerald-700">Purchase</th>
                <th className="px-2 py-2 text-right text-red-700">Consumption</th>
                <th className="px-2 py-2 text-right">Cons.Rev</th>
                <th className="px-2 py-2 text-right">Adj.In</th>
                <th className="px-2 py-2 text-right">Adj.Out</th>
                <th className="px-2 py-2 text-right">Trf.In</th>
                <th className="px-2 py-2 text-right">Trf.Out</th>
                <th className="px-2 py-2 text-right">Damage</th>
                <th className="px-2 py-2 text-right font-bold">Closing</th>
              </tr></thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={13} className="text-center py-8 text-zinc-500 text-sm">No data for this date.</td></tr>}
                {rows.map((r) => (
                  <tr key={`${r.machine_id}-${r.plate_size_id}-${r.location_id}`}>
                    <td className="px-2 py-2 font-mono">{nameOf(m.machines, r.machine_id)}</td>
                    <td className="px-2 py-2 font-mono">{m.plate_sizes.find(p=>p.id===r.plate_size_id)?.canonical || "-"}</td>
                    <td className="px-2 py-2">{nameOf(m.locations, r.location_id)}</td>
                    <td className="px-2 py-2 text-right font-mono">{r.opening}</td>
                    <td className="px-2 py-2 text-right font-mono text-emerald-700">{r.purchase}</td>
                    <td className="px-2 py-2 text-right font-mono text-red-700">{r.consumption}</td>
                    <td className="px-2 py-2 text-right font-mono">{r.consumption_reversal}</td>
                    <td className="px-2 py-2 text-right font-mono">{r.adjustment_in}</td>
                    <td className="px-2 py-2 text-right font-mono">{r.adjustment_out}</td>
                    <td className="px-2 py-2 text-right font-mono">{r.transfer_in}</td>
                    <td className="px-2 py-2 text-right font-mono">{r.transfer_out}</td>
                    <td className="px-2 py-2 text-right font-mono">{r.damage}</td>
                    <td className="px-2 py-2 text-right font-mono font-bold">{r.closing}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
